import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, CheckCircle, ExternalLink, Loader2, Search } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

interface EnterpriseOpcoDetectionProps {
  onOpcoDetected: (opco: string, company: string) => void;
  onContinueWithoutOpco: () => void;
}

interface OpcoData {
  id: string;
  nom: string;
  code: string;
  secteur_activite: string[];
  codes_naf: string[];
  url_portail?: string;
  subrogation_possible: boolean;
  delai_traitement_jours: number;
}

interface UserProfile {
  company: string;
  siret?: string;
  company_address?: string;
}

export function EnterpriseOpcoDetection({ onOpcoDetected, onContinueWithoutOpco }: EnterpriseOpcoDetectionProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [nafCode, setNafCode] = useState('');
  const [opcos, setOpcos] = useState<OpcoData[]>([]);
  const [filteredOpcos, setFilteredOpcos] = useState<OpcoData[]>([]);
  const [selectedOpco, setSelectedOpco] = useState<OpcoData | null>(null);
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();

  // Charger le profil de l'entreprise connectée
  const loadUserProfile = async () => {
    if (authLoading) return;
    
    if (!user) {
      setProfileLoading(false);
      setUserProfile(null);
      return;
    }
    
    try {
      setProfileLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('company, siret, company_address')
        .eq('user_id', user.id)
        .eq('profile_type', 'company')
        .single();

      if (error) {
        console.error('❌ Erreur profil entreprise:', error);
        toast({
          title: "Erreur",
          description: "Impossible de récupérer les informations de votre entreprise",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setUserProfile(data);
      } else {
        toast({
          title: "Profil entreprise non trouvé",
          description: "Votre compte ne semble pas être configuré comme entreprise",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('❌ Erreur:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  // Charger les OPCO depuis Supabase
  const loadOpcos = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('opco_data')
        .select('*')
        .order('nom');

      if (error) {
        console.error('❌ Erreur OPCO:', error);
        // Fallback avec données simulées
        const mockOpcos = [
          {
            id: '1',
            nom: 'ATLAS',
            code: 'ATLAS',
            secteur_activite: ['Services financiers', 'Conseil', 'Informatique', 'Télécommunications'],
            codes_naf: ['6201Z', '6202A', '7022Z'],
            subrogation_possible: true,
            delai_traitement_jours: 15
          },
          {
            id: '2', 
            nom: 'COMMERCE',
            code: 'COMMERCE',
            secteur_activite: ['Commerce de détail', 'Restauration', 'Hôtellerie'],
            codes_naf: ['4711D', '5610A'],
            subrogation_possible: true,
            delai_traitement_jours: 20
          },
          {
            id: '3',
            nom: 'CONSTRUCTYS',
            code: 'CONSTRUCTYS', 
            secteur_activite: ['Construction', 'BTP', 'Travaux publics'],
            codes_naf: ['4110A', '4120A'],
            subrogation_possible: false,
            delai_traitement_jours: 25
          }
        ];
        setOpcos(mockOpcos);
        setFilteredOpcos(mockOpcos);
        return;
      }

      setOpcos(data || []);
      setFilteredOpcos(data || []);
    } catch (error) {
      console.error('❌ Erreur:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les OPCO",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUserProfile();
    loadOpcos();
  }, [user, authLoading]);

  // Recherche et filtrage
  const handleSearch = () => {
    let filtered = opcos;

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(opco => {
        const matchesSector = opco.secteur_activite.some(sector => 
          sector.toLowerCase().includes(search)
        );
        const matchesName = opco.nom.toLowerCase().includes(search);
        const matchesCode = opco.code.toLowerCase().includes(search);
        return matchesSector || matchesName || matchesCode;
      });
    }

    if (nafCode) {
      const code = nafCode.replace(/\./g, '').replace(/[A-Z]/g, '');
      filtered = filtered.filter(opco =>
        opco.codes_naf.some(c => c.replace(/\./g, '').replace(/[A-Z]/g, '').startsWith(code))
      );
    }

    setFilteredOpcos(filtered);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, nafCode, opcos]);

  const handleOpcoSelection = (opco: OpcoData) => {
    setSelectedOpco(opco);
  };

  const confirmSelection = () => {
    if (selectedOpco && userProfile?.company) {
      onOpcoDetected(selectedOpco.nom, userProfile.company);
      toast({
        title: "OPCO sélectionné",
        description: `${selectedOpco.nom} pour ${userProfile.company}`,
      });
    } else {
      toast({
        title: "Sélection manquante",
        description: "Veuillez sélectionner un OPCO",
        variant: "destructive",
      });
    }
  };

  if (profileLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Chargement des informations de votre entreprise...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="space-y-6">
        <Card className="border-destructive/50">
          <CardContent className="text-center py-8">
            <p className="text-destructive">Impossible de récupérer les informations de votre entreprise.</p>
            <Button variant="outline" onClick={onContinueWithoutOpco} className="mt-4">
              Continuer sans OPCO
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Informations entreprise - automatiquement récupérées */}
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-green-800">
            <Building2 className="h-5 w-5" />
            <CheckCircle className="h-4 w-4" />
            Votre entreprise (connectée)
          </CardTitle>
          <CardDescription className="text-green-700">
            Informations récupérées depuis votre profil
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div>
              <Label className="text-sm font-medium">Nom de l'entreprise</Label>
              <p className="text-sm font-semibold">{userProfile.company}</p>
            </div>
            {userProfile.siret && (
              <div>
                <Label className="text-sm font-medium">SIRET</Label>
                <p className="text-sm">{userProfile.siret}</p>
              </div>
            )}
            {userProfile.company_address && (
              <div>
                <Label className="text-sm font-medium">Adresse</Label>
                <p className="text-sm">{userProfile.company_address}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Recherche OPCO */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Trouvez votre OPCO
          </CardTitle>
          <CardDescription>
            Recherchez par secteur d'activité ou code NAF
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="search">Secteur d'activité</Label>
              <Input
                id="search"
                placeholder="Ex: informatique, commerce, construction..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="naf">Code NAF (optionnel)</Label>
              <Input
                id="naf"
                placeholder="Ex: 62.01, 47.11"
                value={nafCode}
                onChange={(e) => setNafCode(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Résultats OPCO */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          OPCO disponibles ({loading ? '...' : filteredOpcos.length})
        </h3>
        
        {loading ? (
          <Card>
            <CardContent className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Chargement...</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {filteredOpcos.map((opco) => (
              <Card 
                key={opco.id}
                className={`cursor-pointer transition-colors ${
                  selectedOpco?.id === opco.id 
                    ? 'ring-2 ring-primary bg-primary/5' 
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => handleOpcoSelection(opco)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-lg">{opco.nom}</h4>
                        <Badge variant="outline" className="text-xs">
                          {opco.code}
                        </Badge>
                        {selectedOpco?.id === opco.id && (
                          <CheckCircle className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="font-medium text-muted-foreground">Secteurs :</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {opco.secteur_activite.slice(0, 3).map((sector) => (
                              <Badge key={sector} variant="secondary" className="text-xs">
                                {sector}
                              </Badge>
                            ))}
                            {opco.secteur_activite.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{opco.secteur_activite.length - 3}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <p className="font-medium text-muted-foreground">Codes NAF :</p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {opco.codes_naf.slice(0, 4).map((code) => (
                              <Badge key={code} variant="outline" className="text-xs">
                                {code}
                              </Badge>
                            ))}
                            {opco.codes_naf.length > 4 && (
                              <Badge variant="outline" className="text-xs">
                                +{opco.codes_naf.length - 4}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>⏱️ {opco.delai_traitement_jours} jours</span>
                        {opco.subrogation_possible && (
                          <span><CheckCircle className="h-3 w-3 inline mr-1" /> Paiement direct possible</span>
                        )}
                        {opco.url_portail && (
                          <Button
                            variant="link"
                            size="sm"
                            className="p-0 h-auto text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(opco.url_portail, '_blank');
                            }}
                          >
                            Site officiel <ExternalLink className="h-3 w-3 ml-1" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && filteredOpcos.length === 0 && opcos.length > 0 && (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">
                Aucun OPCO trouvé pour vos critères.
                <br />
                Essayez avec d'autres mots-clés.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onContinueWithoutOpco}
          className="flex-1"
        >
          Continuer sans OPCO
        </Button>
        <Button
          onClick={confirmSelection}
          disabled={!selectedOpco}
          className="flex-1"
        >
          Confirmer la sélection
        </Button>
      </div>
    </div>
  );
}